import * as React from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  tableCellRecipe,
  tableEmptyRecipe,
  tableHeadRecipe,
  tableHeaderRecipe,
  tableRecipe,
  tableRowRecipe,
  tableScrollRecipe,
  type TableRecipeProps,
  type TableShape,
} from '../recipes/table.recipe';

type Align = 'left' | 'center' | 'right';

/**
 * The shape is set once on <Table> and read by every header, row, head and
 * cell inside, so a table cannot end up with a band header over ruled rows.
 */
const TableShapeContext = React.createContext<TableShape>('grid');
const useShape = () => React.useContext(TableShapeContext);

export interface TableProps
  extends React.TableHTMLAttributes<HTMLTableElement>,
    TableRecipeProps {
  /** Wrap in a horizontally scrolling, bordered container. On by default. */
  scrollable?: boolean;
  /** Border on the scroll container. */
  bordered?: boolean;
}

/**
 * A data table.
 *
 * Composed rather than data-driven — these screens all need custom cells
 * (status badges, row menus, links), and a `columns` prop ends up fighting
 * every one of them.
 *
 *   <Table>
 *     <TableHeader><TableRow><TableHead>Driver</TableHead></TableRow></TableHeader>
 *     <TableBody><TableRow><TableCell>Marcus</TableCell></TableRow></TableBody>
 *   </Table>
 *
 * `shape="band"` is the Table view of a grouped list, beside ListToolbar:
 * the list's rounded bands and 16px rows rather than rules and a frame.
 */
export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  function Table(
    {
      className,
      layout,
      shape = 'grid',
      scrollable = true,
      bordered = true,
      children,
      ...props
    },
    ref
  ) {
    const table = (
      <TableShapeContext.Provider value={shape ?? 'grid'}>
        <table
          ref={ref}
          className={cn(tableRecipe({ layout, shape }), className)}
          {...props}
        >
          {children}
        </table>
      </TableShapeContext.Provider>
    );

    return scrollable ? (
      <div className={tableScrollRecipe({ bordered, shape })}>{table}</div>
    ) : (
      table
    );
  }
);

export interface TableHeaderProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  /**
   * Keep the header visible while the body scrolls. On by default — these
   * tables get long enough that losing the column labels makes them unusable.
   */
  sticky?: boolean;
}

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  TableHeaderProps
>(function TableHeader({ className, sticky = true, ...props }, ref) {
  const shape = useShape();
  return (
    <thead
      ref={ref}
      className={cn(tableHeaderRecipe({ sticky, shape }), className)}
      {...props}
    />
  );
});

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(function TableBody({ className, ...props }, ref) {
  return <tbody ref={ref} className={className} {...props} />;
});

export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(function TableFooter({ className, ...props }, ref) {
  return (
    <tfoot
      ref={ref}
      className={cn(
        'border-t border-border bg-muted/40 font-medium',
        className
      )}
      {...props}
    />
  );
});

export interface TableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  /** Whole row is clickable. Needs an accessible affordance inside too. */
  interactive?: boolean;
  selected?: boolean;
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  function TableRow({ className, interactive, selected, ...props }, ref) {
    const shape = useShape();
    return (
      <tr
        ref={ref}
        data-state={selected ? 'selected' : undefined}
        className={cn(
          tableRowRecipe({ shape, interactive, selected }),
          className
        )}
        {...props}
      />
    );
  }
);

type HideBelow = 'sm' | 'md' | 'lg';

export interface TableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
  /** Right-align to match the numeric cells below. */
  numeric?: boolean;
  /**
   * Show the column only from this breakpoint up. Set the same value on the
   * column's cells, or the rows will drift by one column on a phone.
   */
  hideBelow?: HideBelow;
  /**
   * Keep the column in view while the others scroll under it. `end` pins it
   * to the right edge — the row-action column of a wide table. Set the same
   * value on the column's cells.
   */
  pinned?: 'end';
  /** Current sort direction, if this column is the one being sorted. */
  sort?: 'asc' | 'desc' | false;
  /** Makes the header a sort control and shows the direction indicator. */
  onSort?: () => void;
}

/**
 * A column header. Pass `onSort` to make it sortable — it then renders as a
 * real button with `aria-sort` set, so the sort state is announced rather than
 * conveyed only by a chevron.
 */
export const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  function TableHead(
    {
      className,
      align = 'left',
      numeric,
      hideBelow,
      pinned,
      sort = false,
      onSort,
      children,
      ...props
    },
    ref
  ) {
    const shape = useShape();
    const SortIcon =
      sort === 'asc'
        ? ChevronUp
        : sort === 'desc'
          ? ChevronDown
          : ChevronsUpDown;

    return (
      <th
        ref={ref}
        scope="col"
        aria-sort={
          onSort
            ? sort === 'asc'
              ? 'ascending'
              : sort === 'desc'
                ? 'descending'
                : 'none'
            : undefined
        }
        className={cn(
          tableHeadRecipe({
            shape,
            align,
            numeric,
            hideBelow,
            pinned,
            sortable: Boolean(onSort),
          }),
          className
        )}
        {...props}
      >
        {onSort ? (
          <button
            type="button"
            onClick={onSort}
            className="inline-flex items-center gap-1 outline-none focus-visible:underline"
          >
            {children}
            <SortIcon
              className={cn(
                'size-icon-xs',
                sort ? 'text-foreground' : 'opacity-50'
              )}
              aria-hidden="true"
            />
          </button>
        ) : (
          children
        )}
      </th>
    );
  }
);

export interface TableCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
  /** Right-aligns and uses tabular figures, so columns of money line up. */
  numeric?: boolean;
  /** Clip overflowing content instead of widening the column. */
  truncate?: boolean;
  /** Show the cell only from this breakpoint up; matches its TableHead. */
  hideBelow?: HideBelow;
  /** Pin the cell to the table's edge; matches its TableHead. */
  pinned?: 'end';
  /** The value failed validation: boxed in destructive, like an invalid Input. */
  invalid?: boolean;
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  function TableCell(
    {
      className,
      align = 'left',
      numeric,
      truncate,
      hideBelow,
      pinned,
      invalid,
      ...props
    },
    ref
  ) {
    const shape = useShape();
    return (
      <td
        ref={ref}
        className={cn(
          tableCellRecipe({
            shape,
            align,
            numeric,
            truncate,
            hideBelow,
            pinned,
            invalid,
          }),
          className
        )}
        {...props}
      />
    );
  }
);

export interface TableEmptyProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Must match the number of columns, or the row will not span the table. */
  colSpan: number;
}

/** The "no results" row. Give it the table's column count. */
export const TableEmpty = React.forwardRef<
  HTMLTableCellElement,
  TableEmptyProps
>(function TableEmpty({ className, colSpan, children, ...props }, ref) {
  return (
    <tr>
      <td
        ref={ref}
        colSpan={colSpan}
        className={cn(tableEmptyRecipe(), className)}
        {...props}
      >
        {children}
      </td>
    </tr>
  );
});
